import { DayPilot } from 'daypilot-pro-react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'

import { ScheduleMenuItem } from 'Manager/Schedule/SchedulerContextMenu/helpers'
import { openModal } from 'store/actions/modals'
import { ACTIONS } from 'Manager/Schedule/SchedulerContextMenu/constants'

type Props = {
  clearSelection: (scheduler: DayPilot.MenuItemClickArgs['source']['calendar']) => void
  copySelection: (source: DayPilot.MenuItemClickArgs['source']) => void
  scheduleTemplatesById: MStore.ResourceMap['scheduleTemplatesById']
}
export type SingleItems = { [key: string]: ScheduleMenuItem }

const useSingleItems = ({ clearSelection, copySelection, scheduleTemplatesById }: Props): SingleItems => {
  const { t } = useTranslation('legacy')
  const dispatch = useDispatch()
  const history = useHistory()
  return {
    [ACTIONS.EDIT]: {
      text: t('manager.schedule.CONTEXT_MENU.EDIT_SHIFT'),
      order: 1,
      onClick: ({ source: { data } }) => dispatch(openModal('ScheduleEditModal', { _id: data.id }))
    },
    [ACTIONS.EDIT_USER]: {
      text: t('manager.schedule.CONTEXT_MENU.EDIT_SHIFT'),
      order: 1,
      onClick: ({ source: { data } }) => dispatch(openModal('ScheduleAssignEmployeeModal', { ids: [data.id] }))
    },
    [ACTIONS.PUBLISH]: {
      text: t('manager.schedule.CONTEXT_MENU.PUBLISH'),
      order: 2,
      onClick: ({ source: { data } }) => dispatch(openModal('SchedulePublishModal', data.isTimeOff ? { timeOffIds: [data.id] } : { shiftIds: [data.id] }))
    },
    [ACTIONS.UNPUBLISH]: {
      text: t('manager.schedule.CONTEXT_MENU.UNPUBLISH'),
      order: 2,
      onClick: ({ source: { data } }) => dispatch(openModal('ScheduleUnpublishModal', { shiftIds: [data.id] }))
    },
    [ACTIONS.NOTIFY_TIMEOFF]: {
      text: t('manager.schedule.CONTEXT_MENU.SEND_TIMEOFF_NOTIFICATION'),
      order: 2,
      onClick: ({ source: { data } }) => dispatch(openModal('SchedulePublishModal', { timeOffIds: [data.id], isConfirming: true }))
    },
    [ACTIONS.NOTIFY_SHIFT]: {
      text: t('manager.schedule.CONTEXT_MENU.SEND_SHIFT_NOTIFICATION'),
      order: 3,
      onClick: ({ source: { data } }) => dispatch(openModal('SchedulePublishModal', { shiftIds: [data.id], isConfirmingShift: true }))
    },
    [ACTIONS.CANCEL]: {
      text: t('manager.schedule.CONTEXT_MENU.CANCEL'),
      order: 4,
      onClick: ({ source: { data, calendar: scheduler } }) => {
        dispatch(openModal('ScheduleCancelModal', { ids: [data.id] }))
        clearSelection(scheduler)
      }
    },
    [ACTIONS.RESTORE]: {
      text: t('manager.schedule.CONTEXT_MENU.RESTORE_SHIFT'),
      order: 4,
      onClick: ({ source: { data } }) => dispatch(openModal('ScheduleRestoreModal', { ids: [data.id] }))
    },
    [ACTIONS.COPY]: {
      text: t('manager.schedule.CONTEXT_MENU.COPY'),
      order: 5,
      onClick: ({ source }) => {
        copySelection(source)
      }
    },
    [ACTIONS.GO_TO_PUNCHCARD]: {
      text: t('manager.schedule.CONTEXT_MENU.OPEN_PUNCH_CARD'),
      submenu: 'open',
      order: 1,
      onClick: ({
        source: {
          data: { punchCardId }
        }
      }) => history.push(`/manage/punch/cards/${punchCardId}`)
    },
    [ACTIONS.GO_TO_ATTENDANCE]: {
      text: t('manager.schedule.CONTEXT_MENU.OPEN_ATTENDANCE'),
      submenu: 'open',
      order: 2,
      onClick: ({ source: { data } }) => history.push(`/manage/attendances/${data.attendanceId}`)
    },
    [ACTIONS.GO_TO_TIMEOFF]: {
      text: t('manager.schedule.CONTEXT_MENU.OPEN_TIME_OFF'),
      submenu: 'open',
      order: 3,
      onClick: ({ source: { data } }) => history.push(`/manage/timeOff/${data.timeOffId}`)
    },
    [ACTIONS.OPEN_LEAVE_COMMENTS]: {
      text: t('manager.schedule.CONTEXT_MENU.OPEN_COMMENTS'),
      submenu: 'open',
      order: 4,
      onClick: ({ source: { data } }) => dispatch(openModal('CommentsModal', { resourceId: data.attendanceId, resourceType: 'leave' }))
    },
    [ACTIONS.OPEN_VACATION_COMMENTS]: {
      text: t('manager.schedule.CONTEXT_MENU.OPEN_COMMENTS'),
      submenu: 'open',
      order: 4,
      onClick: ({ source: { data } }) => dispatch(openModal('CommentsModal', { resourceId: data.timeOffId, resourceType: 'vacation' }))
    },
    [ACTIONS.GO_TO_CONTACT_LIST]: {
      text: t('manager.schedule.CONTEXT_MENU.OPEN_THE_SHIFT_CONTACT_LIST'),
      submenu: 'open',
      order: 4,
      onClick: ({
        source: {
          data: { contactListId }
        }
      }) => history.push(`/manage/contact/lists/${contactListId}`)
    },
    [ACTIONS.GO_TO_USER]: {
      text: t('manager.schedule.CONTEXT_MENU.OPEN_USER'),
      submenu: 'open',
      order: 5,
      onClick: ({ source: { data } }) => history.push(`/manage/users/${data.employeeId}`)
    },
    [ACTIONS.GO_TO_USER_SCHEDULE]: {
      text: t('manager.schedule.CONTEXT_MENU.OPEN_USER_SCHEDULE'),
      submenu: 'open',
      order: 6,
      onClick: ({ source: { data } }) => dispatch(openModal('ScheduleOpenEmployeeSchedule', { employeeId: data.employeeId, defaultDate: data.start }))
    },
    [ACTIONS.GO_TO_SCHEDULE_TEMPLATE]: {
      text: t('manager.schedule.CONTEXT_MENU.OPEN_THE_SHIFT_TEMPLATE'),
      submenu: 'open',
      order: 7,
      onClick: ({
        source: {
          data: { templateId }
        }
      }) => {
        const scheduleTemplate = scheduleTemplatesById[templateId]
        if (!scheduleTemplate) return
        if (scheduleTemplate.projects?.length) return history.push(`/manage/projects/${scheduleTemplate.projects[0]}/templates/${templateId}`)
        else history.push(`/manage/schedule/templates/${templateId}`)
      }
    },

    [ACTIONS.DELETE]: {
      text: t('manager.schedule.CONTEXT_MENU.DELETE'),
      submenu: 'advanced',
      order: 1,
      onClick: ({ source: { data, calendar: scheduler } }) => {
        dispatch(openModal('ScheduleDeleteModal', { events: [data] }))
        clearSelection(scheduler)
      }
    },
    [ACTIONS.UNASSIGN_EMPLOYEE]: {
      text: t('manager.schedule.CONTEXT_MENU.UNASSIGN_EMPLOYEE'),
      submenu: 'advanced',
      order: 2,
      onClick: ({ source: { data } }) => dispatch(openModal('ScheduleUnassignEmployeeModal', { ids: [data.id] }))
    },
    [ACTIONS.FINALIZE]: {
      text: t('manager.schedule.CONTEXT_MENU.FINALIZE'),
      submenu: 'advanced',
      order: 3,
      onClick: ({ source: { data } }) => dispatch(openModal('ScheduleFinalizeModal', { ids: [data.id], isFinalizing: true }))
    },
    [ACTIONS.UNFINALIZE]: {
      text: t('manager.schedule.CONTEXT_MENU.UNFINALIZE'),
      submenu: 'advanced',
      order: 3,
      onClick: ({ source: { data } }) => dispatch(openModal('ScheduleFinalizeModal', { ids: [data.id], isFinalizing: false }))
    },
    [ACTIONS.CREATE_LIST]: {
      text: t('manager.schedule.CONTEXT_MENU.CREATE_LIST'),
      submenu: 'advanced',
      order: 4,
      onClick: ({ source: { data } }) => dispatch(openModal('ContactListCreationModal', { ids: [data.id] }))
    },
    [ACTIONS.CREATE_ATTENDANCE]: {
      text: t('manager.schedule.CONTEXT_MENU.CREATE_LEAVE'),
      submenu: 'advanced',
      order: 5,
      onClick: ({ source: { data } }) => dispatch(openModal('ScheduleCreateAttendance', { shift: data }))
    },
    [ACTIONS.CREATE_PUNCHCARD]: {
      text: t('manager.schedule.CONTEXT_MENU.CREATE_PUNCHCARD'),
      submenu: 'advanced',
      order: 6,
      onClick: ({ source: { data } }) => {
        history.push({ pathname: `/manage/punch/cards/new/`, state: { shift: data } })
      }
    }
  } as const
}

export { useSingleItems }
